import type { DrawableTextData } from '../interfaces/drawable-data.interface';
import { remapParagraphRunsForTextChange, remapStyleRunsForTextChange, type StyleRunRange } from './style-run-ranges';

/** True when the runs leave no gap in [0, length): only then is a field they all share a field of the whole text. */
export function runsCoverText(runs: readonly StyleRunRange[], length: number): boolean {
  let covered = 0;
  for (const run of [...runs].sort((a, b) => a.from - b.from)) {
    if (run.from > covered) return false;
    covered = Math.max(covered, run.to);
  }

  return length > 0 && covered >= length;
}

/** The fields every run carries with one and the same value — what a run list says about each of its letters. */
export function sharedRunFields<T extends StyleRunRange>(runs: readonly T[]): Partial<Omit<T, 'from' | 'to'>> {
  const [first, ...rest] = runs;
  const shared: Record<string, unknown> = {};
  if (!first) return shared as Partial<Omit<T, 'from' | 'to'>>;
  for (const [key, value] of Object.entries(first)) {
    if (key === 'from' || key === 'to' || value === undefined) continue;
    if (rest.every(run => (run as Record<string, unknown>)[key] === value)) shared[key] = value;
  }

  return shared as Partial<Omit<T, 'from' | 'to'>>;
}

/**
 * The runs of `oldText` placed on `newText` when code (not typing) swaps the string. One law for every such path:
 * the same text keeps its runs verbatim; an edit that keeps a common start or end follows the diff
 * (`remapStyleRunsForTextChange`) and keeps the split of the runs, except that equal neighbours merge into one run
 * (`mergeSegments`); only when the diff keeps no run (a full replacement) do runs covering the whole old text lend the
 * fields they all share as one run over the whole new text (the typography copy's law for another text); anything
 * else is dropped. `undefined` in → `undefined` out.
 */
export function carryStyleRunsForTextChange<T extends StyleRunRange>(
  runs: readonly T[] | undefined,
  oldText: string,
  newText: string,
): StyleRunRange[] | undefined {
  if (!runs || runs.length === 0) return runs ? [] : undefined;
  if (oldText === newText) return [...runs];
  const remapped = remapStyleRunsForTextChange(runs, oldText, newText) ?? [];
  if (remapped.length > 0 || !runsCoverText(runs, oldText.length)) return remapped;

  const shared = sharedRunFields(runs);
  return newText.length > 0 && Object.keys(shared).length > 0
    ? [{ ...shared, from: 0, to: newText.length } as StyleRunRange]
    : [];
}

/**
 * The drawable with `text` in place of its string. The bag's runs (style and paragraph) index letters of `runsText` —
 * the drawable's own string unless the caller knows better (a cleared pack slot still holding its sample's runs) —
 * and move to `text` by {@link carryStyleRunsForTextChange} / the paragraph diff. An empty list removes the key.
 */
export function withDrawableText<T extends DrawableTextData>(d: T, text: string, runsText = d.text?.text ?? ''): T {
  const runs = carryStyleRunsForTextChange(d.templateMeta?.styleRuns, runsText, text);
  const paragraphRuns = remapParagraphRunsForTextChange(d.templateMeta?.paragraphRuns, runsText, text);

  return {
    ...d,
    text: { ...d.text, text },
    ...((runs === undefined && paragraphRuns === undefined) || !d.templateMeta
      ? {}
      : {
          templateMeta: {
            ...d.templateMeta,
            ...(runs === undefined ? {} : { styleRuns: runs.length > 0 ? [...runs] : undefined }),
            ...(paragraphRuns === undefined ? {} : { paragraphRuns: paragraphRuns.length > 0 ? paragraphRuns : undefined }),
          },
        }),
  };
}
